import { DAY_KINDS, type CalendarDay, type DayKind } from '@hr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeftIcon, ChevronRightIcon, WandSparklesIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Field } from '@/components/form';
import { DecimalInput } from '@/components/money-input';
import { PageHeader } from '@/components/page';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { monthLabel } from '@/lib/months';
import { DAY_KIND_LABELS, uk } from '@/i18n/uk';
import { isHr } from '@/lib/roles';
import { errorMessage, trpc } from '@/lib/trpc';
import { cn, nullable } from '@/lib/utils';

export const Route = createFileRoute('/_app/calendar')({
  validateSearch: z.object({
    year: z.number().int().min(2020).max(2100).optional().catch(undefined),
  }),
  component: CalendarPage,
});

const KIND_STYLE: Record<DayKind, string> = {
  working: 'bg-background',
  short: 'bg-sky-100 dark:bg-sky-950',
  weekend: 'bg-zinc-200 text-muted-foreground dark:bg-zinc-800',
  holiday: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
};

function CalendarPage() {
  const t = uk.time;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const year = search.year ?? new Date().getFullYear();
  const query = useQuery(trpc.calendar.year.queryOptions(year));
  const [generating, setGenerating] = useState(false);
  const setYear = (y: number) => void navigate({ search: { year: y }, replace: true });

  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  const days = query.data?.days ?? [];
  const working = days.filter((d) => d.kind === 'working' || d.kind === 'short');

  return (
    <>
      <PageHeader
        title={t.calendar}
        description={t.calendarHint}
        actions={
          canWrite && (
            <Button onClick={() => setGenerating(true)}>
              <WandSparklesIcon /> {t.generate}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="size-9"
            aria-label={String(year - 1)}
            onClick={() => setYear(year - 1)}
          >
            <ChevronLeftIcon />
          </Button>
          <span className="w-16 text-center text-lg font-semibold">{year}</span>
          <Button
            variant="outline"
            size="icon"
            className="size-9"
            aria-label={String(year + 1)}
            onClick={() => setYear(year + 1)}
          >
            <ChevronRightIcon />
          </Button>
        </div>
        <span className="text-sm text-muted-foreground">
          {t.workingDays}: {working.length} · {t.norm}: {working.reduce((s, d) => s + d.hours, 0)}
        </span>
        <div className="flex flex-wrap gap-2 text-xs">
          {DAY_KINDS.map((k) => (
            <span key={k} className={cn('rounded border px-1.5 py-0.5', KIND_STYLE[k])}>
              {DAY_KIND_LABELS[k]}
            </span>
          ))}
        </div>
      </div>
      {query.data && !query.data.stored && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          {t.notStored}
        </p>
      )}
      {query.isPending ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {months.map((m) => (
            <MonthGrid
              key={m}
              month={m}
              days={days.filter((d) => d.date.startsWith(m))}
              editable={canWrite && Boolean(query.data?.stored)}
            />
          ))}
        </div>
      )}
      {generating && <GenerateDialog year={year} onClose={() => setGenerating(false)} />}
    </>
  );
}

function MonthGrid({
  month,
  days,
  editable,
}: {
  month: string;
  days: CalendarDay[];
  editable: boolean;
}) {
  const first = new Date(`${month}-01T00:00:00Z`).getUTCDay();
  const offset = (first + 6) % 7; // понеділок — перший
  const hours = days.reduce((s, d) => s + d.hours, 0);
  return (
    <section className="rounded-lg border p-3">
      <h3 className="mb-2 flex justify-between text-sm font-medium">
        {monthLabel(month)}
        <span className="text-xs font-normal text-muted-foreground">{hours} год</span>
      </h3>
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'].map((d) => (
          <span key={d} className="text-muted-foreground">
            {d}
          </span>
        ))}
        {Array.from({ length: offset }, (_, i) => (
          <span key={`e${i}`} />
        ))}
        {days.map((d) => (
          <DayCell key={d.date} day={d} editable={editable} />
        ))}
      </div>
    </section>
  );
}

function DayCell({ day, editable }: { day: CalendarDay; editable: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: day.kind,
    hours: nullable(day.hours),
    note: day.note ?? '',
  });
  const save = useMutation(
    trpc.calendar.setDay.mutationOptions({
      onSuccess: () => setOpen(false),
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.calendar.pathFilter()),
    }),
  );
  const cell = (
    <button
      type="button"
      disabled={!editable}
      title={[DAY_KIND_LABELS[day.kind], day.note, `${day.hours} год`].filter(Boolean).join(' · ')}
      className={cn(
        'relative h-8 rounded border text-xs tabular-nums enabled:hover:ring-2 enabled:hover:ring-ring',
        KIND_STYLE[day.kind],
      )}
    >
      {Number(day.date.slice(8))}
      {day.note && (
        <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-rose-500" />
      )}
    </button>
  );
  if (!editable) return cell;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{cell}</PopoverTrigger>
      <PopoverContent className="w-64 space-y-2">
        <p className="text-sm font-medium">{day.date.split('-').reverse().join('.')}</p>
        <Field label={uk.time.dayKind}>
          <Select value={form.kind} onValueChange={(k) => setForm({ ...form, kind: k as DayKind })}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {DAY_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={uk.time.hours}>
          <DecimalInput
            value={form.hours}
            step={0.5}
            onChange={(hours) => setForm({ ...form, hours })}
          />
        </Field>
        <Field label={uk.time.note}>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
        <Button
          size="sm"
          className="w-full"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              date: day.date,
              kind: form.kind,
              hours: form.hours ?? 0,
              note: form.note,
            })
          }
        >
          {uk.common.save}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function GenerateDialog({ year, onClose }: { year: number; onClose: () => void }) {
  const t = uk.time;
  const qc = useQueryClient();
  const [hoursPerDay, setHours] = useState<number | null>(8);
  const [holidaysAreDaysOff, setOff] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const gen = useMutation(
    trpc.calendar.generate.mutationOptions({
      onSuccess: () => {
        toast.success(t.generated);
        onClose();
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.calendar.pathFilter()),
    }),
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t.generateTitle} {year}
          </DialogTitle>
          <DialogDescription>{t.calendarHint}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label={t.hoursPerDay}>
            <DecimalInput value={hoursPerDay} step={0.5} min={1} max={12} onChange={setHours} />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={holidaysAreDaysOff} onCheckedChange={(v) => setOff(v === true)} />
            {t.holidaysOff}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(v === true)} />
            {t.overwrite}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {uk.common.cancel}
          </Button>
          <Button
            disabled={!hoursPerDay || gen.isPending}
            onClick={() =>
              gen.mutate({ year, hoursPerDay: hoursPerDay!, holidaysAreDaysOff, overwrite })
            }
          >
            {t.generate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
